import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { addDays, format, isValid, parseISO } from "date-fns";
import {
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  MapPin,
  MoreHorizontal,
  Plus,
  Printer,
  RefreshCw,
  MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import AppointmentDetailDialog from "@/components/appointment/AppointmentDetailDialog";
import { APT } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { canAccessMainTab, canWriteMainTab } from "@/lib/maintabAccess";
import {
  APPOINTMENT_STATUSES,
  CONFIRMATION_STATUSES,
  NO_SHOW_RATE_UNAVAILABLE,
  appointmentNotes,
  appointmentStatusLabel,
  appointmentStatusTone,
  appointmentVisitType,
  confirmationStatusLabel,
  filterAppointmentsForDay,
  formatAppointmentTime,
  listAppointments,
  newAppointmentPath,
  patientCalendarLabel,
  providerDisplay,
  referringProviderDisplay,
  sexLetter,
  actorDisplay,
} from "@/lib/appointmentApi";
import { fetchLocations, fetchPatients, patientNameOf } from "@/lib/encounterApi";
import { ageFromBirthDate } from "@/lib/patientChart";
import "@/styles/emr-appointments-calendar.css";

const STATUS_STYLE = {
  booked: "bg-sky-100 text-sky-800",
  arrived: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-slate-200 text-slate-600",
  noshow: "bg-red-100 text-red-700",
  fulfilled: "bg-emerald-100 text-emerald-800",
};

const CONFIRM_STYLE = {
  confirmed: "bg-emerald-100 text-emerald-800",
  pending: "bg-orange-100 text-orange-800",
  unconfirmed: "bg-slate-100 text-slate-700",
};

const STATUS_DOT = {
  booked: "bg-sky-500",
  arrived: "bg-emerald-600",
  cancelled: "bg-slate-400",
  noshow: "bg-red-500",
};

const CONFIRM_DOT = {
  confirmed: "bg-emerald-500",
  pending: "bg-orange-500",
  unconfirmed: "bg-slate-400",
};

function todayKey() {
  return format(new Date(), "yyyy-MM-dd");
}

function shiftDate(dateKey, days) {
  const parsed = parseISO(dateKey);
  if (!isValid(parsed)) return todayKey();
  return format(addDays(parsed, days), "yyyy-MM-dd");
}

function formatHeaderDate(dateKey) {
  const parsed = parseISO(dateKey);
  return isValid(parsed) ? format(parsed, "EEEE, MMMM d, yyyy") : dateKey;
}

const RECT_CONTROL =
  "h-9 rounded-none border border-slate-200 bg-[#FFFFFF] text-slate-700 shadow-none hover:bg-slate-50 hover:text-slate-900";
const RECT_ICON =
  "h-9 w-9 rounded-none border border-slate-200 bg-[#FFFFFF] text-slate-700 shadow-none hover:bg-slate-50 hover:text-slate-900";
const PRIMARY_ACTION = "h-9 rounded-none bg-teal-700 text-white shadow-none hover:bg-teal-800";
const STATUS_BADGE = "rounded-none border-0";

export default function AppointmentsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, context } = useAuth();
  const canWrite = canWriteMainTab(user?.maintabAccess, "appointments");
  const canWriteEncounters = canWriteMainTab(user?.maintabAccess, "encounters");
  const canReadPatients = canAccessMainTab(user?.maintabAccess, "patients");
  const canReadEncounters = canAccessMainTab(user?.maintabAccess, "encounters");
  const [dateKey, setDateKey] = useState(() => searchParams.get("date") || todayKey());
  const [locationId, setLocationId] = useState(() => searchParams.get("location") || context?.location?.id || "all");
  const [items, setItems] = useState([]);
  const [patients, setPatients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const patientById = useMemo(
    () => Object.fromEntries(patients.map((p) => [p.id, p])),
    [patients],
  );
  const locationById = useMemo(
    () => Object.fromEntries(locations.map((l) => [l.id, l])),
    [locations],
  );

  const loadPatientsAndLocations = useCallback(async () => {
    const [plist, llist] = await Promise.all([
      canReadPatients ? fetchPatients() : Promise.resolve([]),
      fetchLocations(),
    ]);
    setPatients(plist);
    setLocations(llist);
  }, [canReadPatients]);

  const loadAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const params = locationId && locationId !== "all" ? { location: locationId } : {};
      const data = await listAppointments(params);
      setItems(data.items || []);
    } catch {
      toast.error("Failed to load appointments.");
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    loadPatientsAndLocations().catch(() => toast.error("Failed to load calendar reference data."));
  }, [loadPatientsAndLocations]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  useEffect(() => {
    const date = searchParams.get("date");
    if (date && /^\d{4}-\d{2}-\d{2}$/.test(date)) setDateKey(date);
    const loc = searchParams.get("location");
    if (loc) setLocationId(loc);
  }, [searchParams]);

  const rows = useMemo(
    () => filterAppointmentsForDay(items, dateKey, locationId),
    [items, dateKey, locationId],
  );

  const patientLabelFor = (appointment) => {
    const patient = patientById[appointment.patientId];
    return patientCalendarLabel({
      name: patientNameOf(patient) || actorDisplay(appointment, "Patient/") || "—",
      age: patient ? ageFromBirthDate(patient.birthDate) : null,
      sex: patient ? sexLetter(patient.gender) : "",
    });
  };

  const selectedDate = parseISO(dateKey);

  return (
    <div data-testid={APT.page} className="emr-apt-cal min-w-0 space-y-4 overflow-x-hidden">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h1 data-testid={APT.title} className="text-3xl font-semibold tracking-tight text-slate-900">
            Appointment Calendar
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Day view of scheduled appointments. Clinical Encounters are started separately.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" className={RECT_CONTROL} onClick={() => window.print()}>
            <Printer className="h-4 w-4" />
            Print
          </Button>
          <Button
            variant="outline"
            className={RECT_CONTROL}
            onClick={() => toast.info("Open an appointment, then click Send SMS for the SANDBOX test.")}
          >
            <MessageSquare className="h-4 w-4" />
            Message
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className={RECT_CONTROL}>
                More
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="emr-apt-cal-popper rounded-none">
              <DropdownMenuItem disabled>Provider filter — later</DropdownMenuItem>
              <DropdownMenuItem disabled>Visit type filter — later</DropdownMenuItem>
              <DropdownMenuItem disabled>Status filter — later</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="emr-apt-surface flex min-w-0 flex-col gap-2 border border-slate-200 bg-[#FFFFFF] p-2.5 shadow-sm lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          <div className="flex min-w-0 items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              data-testid={APT.datePrev}
              aria-label="Previous day"
              className={`${RECT_ICON} shrink-0`}
              onClick={() => setDateKey((d) => shiftDate(d, -1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  data-testid={APT.datePicker}
                  className={`${RECT_CONTROL} min-w-0 flex-1 justify-between font-medium sm:min-w-[240px] sm:flex-none`}
                >
                  <span className="flex min-w-0 items-center gap-2 truncate">
                    <CalendarDays className="h-4 w-4 shrink-0 text-slate-500" />
                    <span className="truncate">{formatHeaderDate(dateKey)}</span>
                  </span>
                  <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="emr-apt-cal-popper w-auto rounded-none p-0" align="start">
                <Calendar
                  mode="single"
                  className="rounded-none"
                  classNames={{
                    nav_button: "rounded-none",
                    head_cell: "rounded-none",
                    cell: "[&:has([aria-selected])]:rounded-none",
                    day: "rounded-none",
                  }}
                  selected={isValid(selectedDate) ? selectedDate : undefined}
                  onSelect={(day) => day && setDateKey(format(day, "yyyy-MM-dd"))}
                />
              </PopoverContent>
            </Popover>
            <Button
              variant="outline"
              size="icon"
              data-testid={APT.dateNext}
              aria-label="Next day"
              className={`${RECT_ICON} shrink-0`}
              onClick={() => setDateKey((d) => shiftDate(d, 1))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              data-testid={APT.dateToday}
              className={`${RECT_CONTROL} shrink-0`}
              onClick={() => setDateKey(todayKey())}
            >
              Today
            </Button>
          </div>
          <div className="flex min-w-0 items-center gap-2">
            <Select value={locationId} onValueChange={setLocationId}>
              <SelectTrigger
                data-testid={APT.locationFilter}
                className="h-9 w-full min-w-0 rounded-none border-slate-200 bg-[#FFFFFF] shadow-none sm:w-[220px]"
              >
                <MapPin className="mr-1 h-4 w-4 shrink-0 text-slate-500" />
                <SelectValue placeholder="Location" />
              </SelectTrigger>
              <SelectContent className="emr-apt-cal-popper rounded-none">
                <SelectItem value="all">All locations</SelectItem>
                {locationId !== "all" && !locations.some((l) => l.id === locationId) ? (
                  <SelectItem value={locationId}>{context?.location?.name || "Current location"}</SelectItem>
                ) : null}
                {locations.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id}>
                    {loc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="icon"
              data-testid={APT.refreshBtn}
              aria-label="Refresh"
              className={`${RECT_ICON} shrink-0`}
              onClick={loadAppointments}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex overflow-hidden border border-slate-200">
            <Button data-testid={APT.dayView} className="h-9 rounded-none bg-teal-700 px-4 hover:bg-teal-800">
              Day
            </Button>
            <Button data-testid={APT.weekView} variant="ghost" className="h-9 rounded-none text-slate-400" disabled>
              Week
            </Button>
            <Button data-testid={APT.monthView} variant="ghost" className="h-9 rounded-none text-slate-400" disabled>
              Month
            </Button>
          </div>
          {canWrite ? (
          <Button
            data-testid={APT.newBtn}
            onClick={() => navigate(newAppointmentPath(locationId))}
            className={PRIMARY_ACTION}
          >
            <Plus className="h-4 w-4" />
            New Appointment
          </Button>
          ) : null}
        </div>
      </div>

      <div className="emr-apt-surface min-w-0 overflow-hidden border border-slate-200 bg-[#FFFFFF] shadow-sm">
        {loading ? (
          <div data-testid={APT.loading} className="p-8 text-center text-sm text-slate-400">
            Loading appointments…
          </div>
        ) : !rows.length ? (
          <div data-testid={APT.emptyState} className="flex flex-col items-center gap-3 p-10 text-center">
            <CalendarDays className="h-10 w-10 text-slate-300" />
            <p className="text-sm text-slate-500">No appointments for this day and location.</p>
            {canWrite ? (
            <Button className={PRIMARY_ACTION} onClick={() => navigate(newAppointmentPath(locationId))}>
              Schedule an appointment
            </Button>
            ) : null}
          </div>
        ) : (
          <div className="min-w-0 overflow-x-auto">
          <Table data-testid={APT.table} className="min-w-[1080px]">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="h-9 w-[110px]">Time</TableHead>
                <TableHead className="h-9">Status</TableHead>
                <TableHead className="h-9">Confirm</TableHead>
                <TableHead className="h-9">Patient (Age/Sex)</TableHead>
                <TableHead className="h-9">Visit Type</TableHead>
                <TableHead className="h-9">Notes</TableHead>
                <TableHead className="h-9">No-Show %</TableHead>
                <TableHead className="h-9">Provider</TableHead>
                <TableHead className="h-9">Referring Provider</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const label = patientLabelFor(row);
                return (
                  <TableRow
                    key={row.id}
                    data-testid={`${APT.row}-${row.id}`}
                    role="button"
                    tabIndex={0}
                    aria-label={`Appointment for ${label} at ${formatAppointmentTime(row.start)}`}
                    className="cursor-pointer"
                    onClick={() => setSelected(row)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setSelected(row);
                      }
                    }}
                  >
                    <TableCell className="whitespace-nowrap py-1.5 font-medium text-slate-800">
                      {formatAppointmentTime(row.start)}
                    </TableCell>
                    <TableCell className="py-1.5">
                      <Badge className={`${STATUS_BADGE} ${STATUS_STYLE[appointmentStatusTone(row.status)] || "bg-slate-100 text-slate-700"} hover:opacity-100`}>
                        {row.statusLabel || appointmentStatusLabel(row.status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-1.5">
                      <Badge
                        className={`${STATUS_BADGE} ${CONFIRM_STYLE[row.confirmationStatus] || "bg-slate-100 text-slate-600"} hover:opacity-100`}
                      >
                        {confirmationStatusLabel(row.confirmationStatus)}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-1.5 font-medium text-slate-900">
                      {row.patientId && canReadPatients ? (
                        <Link
                          to={`/patients/${row.patientId}`}
                          className="hover:text-teal-800 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {label}
                        </Link>
                      ) : (
                        label
                      )}
                    </TableCell>
                    <TableCell className="py-1.5 text-slate-600">{appointmentVisitType(row) || "—"}</TableCell>
                    <TableCell className="max-w-[220px] truncate py-1.5 text-slate-600" title={appointmentNotes(row)}>
                      {appointmentNotes(row) || "—"}
                    </TableCell>
                    <TableCell className="py-1.5 text-slate-400">{NO_SHOW_RATE_UNAVAILABLE}</TableCell>
                    <TableCell className="py-1.5 text-slate-700">{providerDisplay(row) || "—"}</TableCell>
                    <TableCell className="py-1.5 text-slate-700">{referringProviderDisplay(row) || "—"}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </div>
        )}

        <div className="flex flex-col gap-2 border-t border-slate-200 bg-slate-50 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
          <div data-testid={APT.legend} className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-slate-600">
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-semibold text-slate-700">Appointment</span>
              {APPOINTMENT_STATUSES.map((status) => (
                <span key={status.code} className="inline-flex items-center gap-1.5">
                  <span className={`h-2.5 w-2.5 ${STATUS_DOT[status.code] || "bg-slate-400"}`} />
                  {status.display}
                </span>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-semibold text-slate-700">Confirmation</span>
              {CONFIRMATION_STATUSES.map((status) => (
                <span key={status.code} className="inline-flex items-center gap-1.5">
                  <span className={`h-2.5 w-2.5 ${CONFIRM_DOT[status.code] || "bg-slate-400"}`} />
                  {status.display}
                </span>
              ))}
            </div>
          </div>
          <div data-testid={APT.total} className="text-sm font-medium text-slate-700">
            Total Appointments: {loading ? "—" : rows.length}
          </div>
        </div>
      </div>

      <AppointmentDetailDialog
        appointment={selected}
        patientLabel={selected ? patientLabelFor(selected) : ""}
        locationName={selected ? locationById[selected.locationId]?.name : ""}
        dateLabel={formatHeaderDate(dateKey)}
        onClose={() => setSelected(null)}
        onChanged={loadAppointments}
        editOptions={{ locationId, date: dateKey }}
        canWriteAppointments={canWrite}
        canWriteEncounters={canWriteEncounters}
        canAccessPatients={canReadPatients}
        canAccessEncounters={canReadEncounters}
        chrome="calendar"
      />
    </div>
  );
}
